const services = new Map();
export const ROUTINE_TIME_ZONE = "Asia/Kolkata";
export const dayKey = (time = Date.now()) => new Intl.DateTimeFormat("en-CA", {
  timeZone: ROUTINE_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit",
}).format(new Date(time));
export const newRunId = () => window.crypto?.randomUUID?.() || `run-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function countDailyCompletions(events, presetId, day) {
  const daily = Object.values(events).filter((event) => event.presetId === presetId && event.day === day);
  const cleared = new Set(daily.filter((event) => event.type === "reset").flatMap((event) => event.resetRunIds || []));
  return daily.filter((event) => event.type !== "reset" && !cleared.has(event.runId)).length;
}

export function createRoutineCompletions(uid, { storage = localStorage, backend, now = Date.now } = {}) {
  const prefix = `routine-completions:${uid}:`;
  const listeners = new Set();
  let events = {};
  let pending = {};
  let errors = {};
  let saving = new Set();
  let snapshot;
  let backendPromise;
  const getBackend = () => backendPromise || (backendPromise = (backend ? Promise.resolve(backend) : import("./routineCompletionBackend"))
    .catch((error) => { backendPromise = null; throw error; }));
  const publish = () => {
    snapshot = { events: { ...events, ...pending }, pending: { ...pending }, errors: { ...errors }, saving: new Set(saving), today: dayKey(now()) };
    listeners.forEach((listener) => listener(snapshot));
  };
  const restore = () => {
    try {
      events = JSON.parse(storage.getItem(`${prefix}cache`) || "{}");
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key.startsWith(`${prefix}pending:`)) {
          const event = JSON.parse(storage.getItem(key));
          pending[event.runId] = event;
        }
      }
    } catch { errors.storage = "Could not read local completion records."; }
  };
  restore();
  publish();
  const retry = async (runId) => {
    const ids = runId ? [runId] : Object.keys(pending);
    await Promise.all(ids.map(async (id) => {
      const event = pending[id];
      if (!event || saving.has(id)) return;
      saving.add(id);
      delete errors[id];
      publish();
      try {
        storage.setItem(`${prefix}pending:${id}`, JSON.stringify(event));
        const api = await getBackend();
        if (navigator.onLine === false) throw new Error("Offline");
        let timeout;
        try {
          await Promise.race([
            api.saveCompletion(uid, event),
            new Promise((resolve, reject) => { timeout = setTimeout(() => reject(new Error("Sync timed out")), 15000); }),
          ]);
        } finally { clearTimeout(timeout); }
        events[id] = event;
        // Cache before removing the durable queue entry.
        storage.setItem(`${prefix}cache`, JSON.stringify(events));
        storage.removeItem(`${prefix}pending:${id}`);
        delete pending[id];
      } catch { errors[id] = "Not synced — retry saving this completion."; }
      saving.delete(id);
      publish();
    }));
  };
  const record = (event) => {
    if (events[event.runId] || pending[event.runId]) return;
    const completedAt = now();
    const completion = { ...event, completedAt, day: dayKey(completedAt) };
    pending[event.runId] = completion;
    try { storage.setItem(`${prefix}pending:${event.runId}`, JSON.stringify(completion)); }
    catch { errors[event.runId] = "Local storage unavailable — keep this page open and retry."; publish(); return; }
    publish();
    void retry(event.runId);
  };
  let stop;
  let subscribers = 0;
  const subscribe = (listener) => {
    listeners.add(listener);
    listener(snapshot);
    subscribers++;
    if (subscribers === 1) {
      let cancelled = false;
      getBackend().then((api) => {
        if (cancelled) return;
        const unsubscribe = api.watchCompletions(uid, (remote) => {
          delete errors.connection;
          events = { ...events, ...remote };
          try { storage.setItem(`${prefix}cache`, JSON.stringify(events)); } catch { /* Pending entries remain durable. */ }
          publish();
        }, () => { errors.connection = "Live updates unavailable."; publish(); });
        stop = () => { cancelled = true; unsubscribe(); };
      }).catch(() => { errors.connection = "Live updates unavailable."; publish(); });
      stop = () => { cancelled = true; };
      window.addEventListener("online", retryOnline);
      window.addEventListener("storage", readOtherTab);
      const timer = setInterval(publish, 30000);
      const previousStop = () => stop?.();
      cleanup = () => { previousStop(); clearInterval(timer); window.removeEventListener("online", retryOnline); window.removeEventListener("storage", readOtherTab); };
      void retry();
    }
    return () => { listeners.delete(listener); if (--subscribers === 0) cleanup?.(); };
  };
  const retryOnline = () => { void retry(); };
  const readOtherTab = (event) => { if (event.key?.startsWith(prefix)) { restore(); publish(); void retry(); } };
  let cleanup;
  return { subscribe, getSnapshot: () => snapshot, record, retry };
}
export function getRoutineCompletions(uid) {
  if (!services.has(uid)) services.set(uid, createRoutineCompletions(uid));
  return services.get(uid);
}
